import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

interface SidebarState {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  expandedItems: Set<string>;
}

interface SidebarContextValue extends SidebarState {
  toggleCollapsed: () => void;
  setCollapsed: (collapsed: boolean) => void;
  toggleMobileOpen: () => void;
  setMobileOpen: (open: boolean) => void;
  toggleExpanded: (itemId: string) => void;
  isExpanded: (itemId: string) => boolean;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

const STORAGE_KEY = 'tark_admin_sidebar';

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

interface SidebarProviderProps {
  children: ReactNode;
}

export function SidebarProvider({ children }: SidebarProviderProps) {
  // Initialize from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored).isCollapsed ?? false;
      } catch {
        return false;
      }
    }
    return false;
  });

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return new Set(parsed.expandedItems ?? []);
      } catch {
        return new Set();
      }
    }
    return new Set();
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        isCollapsed,
        expandedItems: Array.from(expandedItems),
      })
    );
  }, [isCollapsed, expandedItems]);

  // Close mobile sidebar on route change or resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev: boolean) => !prev);
  }, []);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsed(collapsed);
  }, []);

  const toggleMobileOpen = useCallback(() => {
    setIsMobileOpen((prev: boolean) => !prev);
  }, []);

  const setMobileOpenFn = useCallback((open: boolean) => {
    setIsMobileOpen(open);
  }, []);

  const toggleExpanded = useCallback((itemId: string) => {
    setExpandedItems((prev: Set<string>) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  const isExpanded = useCallback(
    (itemId: string) => expandedItems.has(itemId),
    [expandedItems]
  );

  const value: SidebarContextValue = {
    isCollapsed,
    isMobileOpen,
    expandedItems,
    toggleCollapsed,
    setCollapsed,
    toggleMobileOpen,
    setMobileOpen: setMobileOpenFn,
    toggleExpanded,
    isExpanded,
  };

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}
